import * as React from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, GitMerge, Loader2, Rocket, ShieldCheck, Undo2, Wrench } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/useToast";
import {
  useApproveDestructiveMigrations,
  useDeployPullRequest,
  useMergePullRequest,
  useResolvePullRequestConflicts,
  useSendBackPullRequest,
} from "@/hooks/queries/use-pull-requests";
import {
  SEND_BACK_NOTES_MAX_LENGTH,
  type ClassifiedMigration,
  type DeployEnvironment,
  type FixJobTarget,
  type MergeMethod,
  type PullRequestView,
} from "@/lib/api/pull-requests";
import { FixTargetPicker } from "./fix-target-picker";
import {
  MERGE_BLOCKED_MESSAGES,
  MERGE_METHOD_LABELS,
  apiErrorInfo,
  mergeBlockedReason,
  shortSha,
} from "./pull-request-status";

function ErrorLine({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="text-sm text-destructive">
      {message}
    </p>
  );
}

// -----------------------------------------------------------------------------
// Merge
// -----------------------------------------------------------------------------

export function MergeDialog({ pr }: { pr: PullRequestView }) {
  const [open, setOpen] = React.useState(false);
  const methods = pr.allowedMergeMethods;
  const [method, setMethod] = React.useState<MergeMethod | null>(null);
  const [migrationsApplied, setMigrationsApplied] = React.useState(false);
  const [backupTaken, setBackupTaken] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const merge = useMergePullRequest(pr.id);
  const { toast } = useToast();

  const blocked = mergeBlockedReason(pr);
  const chosen: MergeMethod | undefined = method && methods.includes(method) ? method : methods[0];
  const hasMigrations = pr.migrationFiles.length > 0;
  // ENG-421: when the project applies migrations automatically on merge, the
  // deploy takes the backup and applies them — no manual confirmations. Only
  // destructive ones need the project owner's approval for the current head.
  const autoApply = hasMigrations && pr.autoApplyMigrations === true;
  const destructive = autoApply ? (pr.migrations ?? []).filter((m) => m.safety === "destructive") : [];
  const destructiveApproved = pr.destructiveApproval?.current === true;
  const needsMigrationConfirm = hasMigrations && !autoApply;
  // The manual path asks for a backup whenever there are migrations. The
  // server only enforces it for destructive ones.
  const needsBackupConfirm = needsMigrationConfirm;
  const canMerge =
    !merge.isPending &&
    !!chosen &&
    (!needsMigrationConfirm || migrationsApplied) &&
    (!needsBackupConfirm || backupTaken) &&
    (destructive.length === 0 || destructiveApproved);

  const handleMerge = async () => {
    if (!chosen || !canMerge) return;
    setError(null);
    try {
      await merge.mutateAsync({
        method: chosen,
        expectedHeadSha: pr.headSha,
        ...(needsMigrationConfirm ? { confirmMigrationsApplied: true } : {}),
        ...(needsBackupConfirm ? { confirmBackupTaken: true } : {}),
      });
      setOpen(false);
      toast("Merged");
    } catch (err) {
      const info = apiErrorInfo(err);
      setError((info.reason && MERGE_BLOCKED_MESSAGES[info.reason]) ?? info.message);
    }
  };

  return (
    <div className="space-y-1">
      <Dialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            setError(null);
            setMigrationsApplied(false);
            setBackupTaken(false);
          }
        }}
      >
        <DialogTrigger asChild>
          <Button size="sm" disabled={blocked !== null}>
            <GitMerge className="h-4 w-4 mr-2" />
            Merge
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge this change</DialogTitle>
            <DialogDescription>
              Merges <code>{pr.headRef}</code> into <code>{pr.baseRef}</code> at commit {shortSha(pr.headSha)}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {methods.length > 1 ? (
              <div className="space-y-1.5">
                <Label>How to merge</Label>
                <Select value={chosen ?? ""} onValueChange={(v) => { setMethod(v as MergeMethod); }}>
                  <SelectTrigger aria-label="Merge method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {methods.map((m) => (
                      <SelectItem key={m} value={m}>
                        {MERGE_METHOD_LABELS[m]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : chosen ? (
              <p className="text-sm text-muted-foreground">{MERGE_METHOD_LABELS[chosen]}</p>
            ) : null}

            {autoApply && (
              <AutoMigrationStatus pr={pr} destructive={destructive} approved={destructiveApproved} />
            )}

            {needsMigrationConfirm && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2">
                <p className="text-sm">
                  This change updates the database structure. Back up the live database and apply these changes
                  to it before merging, or the app may break (or lose data) once it's deployed:
                </p>
                <ul className="text-xs font-mono list-disc pl-5">
                  {pr.migrationFiles.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="confirm-migrations"
                    checked={migrationsApplied}
                    onCheckedChange={(v) => { setMigrationsApplied(v); }}
                  />
                  <Label htmlFor="confirm-migrations" className="text-sm font-normal leading-snug">
                    I have applied the database changes to the live database
                  </Label>
                </div>
                {needsBackupConfirm && (
                  <div className="flex items-start gap-2">
                    <Checkbox
                      id="confirm-backup"
                      checked={backupTaken}
                      onCheckedChange={(v) => { setBackupTaken(v); }}
                    />
                    <Label htmlFor="confirm-backup" className="text-sm font-normal leading-snug">
                      I have backed up the live database
                    </Label>
                  </div>
                )}
              </div>
            )}
            <ErrorLine message={error} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); }}>
              Cancel
            </Button>
            <Button onClick={() => { void handleMerge(); }} disabled={!canMerge}>
              {merge.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {blocked && <p className="text-xs text-muted-foreground">{blocked}</p>}
    </div>
  );
}

/**
 * ENG-421: the merge screen's migration status for projects that apply
 * migrations automatically. Additive changes need nothing; destructive ones
 * need the project owner's approval for the current head.
 */
function AutoMigrationStatus({
  pr,
  destructive,
  approved,
}: {
  pr: PullRequestView;
  destructive: ClassifiedMigration[];
  approved: boolean;
}) {
  const approve = useApproveDestructiveMigrations(pr.id);
  const [error, setError] = React.useState<string | null>(null);
  const count = pr.migrationFiles.length;
  const changes = count === 1 ? "1 database change" : `${String(count)} database changes`;

  const handleApprove = async () => {
    setError(null);
    try {
      await approve.mutateAsync({ expectedHeadSha: pr.headSha });
    } catch (err) {
      const info = apiErrorInfo(err);
      setError((info.reason && MERGE_BLOCKED_MESSAGES[info.reason]) ?? info.message);
    }
  };

  if (destructive.length === 0) {
    return (
      <p className="flex items-start gap-2 text-sm text-muted-foreground" data-testid="migration-status">
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-green-600" />
        <span>
          {changes} will be applied automatically after merge. The database is backed up first.
        </span>
      </p>
    );
  }

  return (
    <div
      className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 space-y-2"
      data-testid="migration-status"
    >
      <p className="text-sm">
        {changes} will be applied automatically after merge. The database is backed up first, but{" "}
        {destructive.length === 1 ? "this change" : "these changes"} can lose data:
      </p>
      <ul className="text-xs list-disc pl-5 space-y-1">
        {destructive.map((m) => (
          <li key={m.path}>
            <span className="font-mono">{m.path}</span>
            {m.reasons.length > 0 && <span className="text-muted-foreground"> — {m.reasons.join("; ")}</span>}
          </li>
        ))}
      </ul>
      {approved && pr.destructiveApproval ? (
        <p className="flex items-center gap-2 text-sm">
          <ShieldCheck className="h-4 w-4 text-green-600" />
          Approved by the project owner for commit {shortSha(pr.destructiveApproval.sha)}.
        </p>
      ) : pr.canApproveDestructive === true ? (
        <Button size="sm" variant="outline" onClick={() => { void handleApprove(); }} disabled={approve.isPending}>
          {approve.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Approve database changes
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">
          {pr.destructiveApproval
            ? "New commits were pushed since the project owner approved. They need to approve again before this can be merged."
            : "The project owner needs to approve these changes before this can be merged."}
        </p>
      )}
      <ErrorLine message={error} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Send back / resolve conflicts
// -----------------------------------------------------------------------------

function useFixJobDone(onDone: () => void) {
  const navigate = useNavigate();
  const { toast } = useToast();
  return (jobId: string, message: string) => {
    onDone();
    toast(message);
    void navigate(`/mailbox/jobs/${jobId}`);
  };
}

export function SendBackDialog({ pr, disabledReason }: { pr: PullRequestView; disabledReason: string | null }) {
  const [open, setOpen] = React.useState(false);
  const [notes, setNotes] = React.useState("");
  const [target, setTarget] = React.useState<FixJobTarget | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const sendBack = useSendBackPullRequest(pr.id);
  const done = useFixJobDone(() => {
    setOpen(false);
    setNotes("");
  });

  const trimmed = notes.trim();
  const tooLong = notes.length > SEND_BACK_NOTES_MAX_LENGTH;
  const canSend = !sendBack.isPending && trimmed.length > 0 && !tooLong && target !== null;

  const handleSend = async () => {
    if (!canSend) return;
    setError(null);
    try {
      const job = await sendBack.mutateAsync({ notes: trimmed, target });
      done(job.id, "Sent back to the agent");
    } catch (err) {
      setError(apiErrorInfo(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabledReason !== null} title={disabledReason ?? undefined}>
          <Undo2 className="h-4 w-4 mr-2" />
          Send back
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send back to the agent</DialogTitle>
          <DialogDescription>
            Tell the agent what to change. It works on the same branch and updates this pull request.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="send-back-notes">What should change?</Label>
            <Textarea
              id="send-back-notes"
              value={notes}
              onChange={(e) => { setNotes(e.target.value); }}
              placeholder="e.g. The save button should stay disabled until the form is valid"
              rows={5}
              maxLength={SEND_BACK_NOTES_MAX_LENGTH}
            />
            <p className="text-xs text-muted-foreground text-right">
              {notes.length}/{SEND_BACK_NOTES_MAX_LENGTH}
            </p>
          </div>
          <FixTargetPicker repoFullName={pr.repoFullName} active={open} onChange={setTarget} />
          <ErrorLine message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={() => { void handleSend(); }} disabled={!canSend}>
            {sendBack.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResolveConflictsDialog({
  pr,
  disabledReason,
}: {
  pr: PullRequestView;
  disabledReason: string | null;
}) {
  const [open, setOpen] = React.useState(false);
  const [target, setTarget] = React.useState<FixJobTarget | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const resolve = useResolvePullRequestConflicts(pr.id);
  const done = useFixJobDone(() => { setOpen(false); });
  const canSend = !resolve.isPending && target !== null;

  const handleSend = async () => {
    if (!canSend) return;
    setError(null);
    try {
      const job = await resolve.mutateAsync({ target });
      done(job.id, "The agent is resolving the conflicts");
    } catch (err) {
      setError(apiErrorInfo(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setError(null);
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" disabled={disabledReason !== null} title={disabledReason ?? undefined}>
          <Wrench className="h-4 w-4 mr-2" />
          Resolve conflicts
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Resolve conflicts</DialogTitle>
          <DialogDescription>
            The agent brings in the latest <code>{pr.baseRef}</code>, fixes the clashing changes and updates this
            pull request.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <FixTargetPicker repoFullName={pr.repoFullName} active={open} onChange={setTarget} />
          <ErrorLine message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={() => { void handleSend(); }} disabled={!canSend}>
            {resolve.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Resolve conflicts
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Deploy
// -----------------------------------------------------------------------------

const ENVIRONMENT_LABELS: Record<DeployEnvironment, string> = {
  dev: "Development",
  stage: "Staging",
  prod: "Production",
};

export function DeployDialog({ pr }: { pr: PullRequestView }) {
  const environments: DeployEnvironment[] = pr.canDeployProd ? ["dev", "stage", "prod"] : ["dev", "stage"];
  const [open, setOpen] = React.useState(false);
  const [environment, setEnvironment] = React.useState<DeployEnvironment>("dev");
  const [confirmProd, setConfirmProd] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const deploy = useDeployPullRequest(pr.id);
  const { toast } = useToast();

  const isProd = environment === "prod";
  const canDeploy = !deploy.isPending && (!isProd || confirmProd);

  const handleDeploy = async () => {
    if (!canDeploy) return;
    setError(null);
    try {
      await deploy.mutateAsync({ environment, ...(isProd ? { confirm: true } : {}) });
      setOpen(false);
      toast(`Deploying to ${ENVIRONMENT_LABELS[environment]}`);
    } catch (err) {
      setError(apiErrorInfo(err).message);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setError(null);
          setConfirmProd(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm">
          <Rocket className="h-4 w-4 mr-2" />
          Deploy
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Deploy this change</DialogTitle>
          <DialogDescription>Deploys the merged commit {shortSha(pr.mergedSha)} to an environment.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Environment</Label>
            <Select
              value={environment}
              onValueChange={(v) => {
                setEnvironment(v as DeployEnvironment);
                setConfirmProd(false);
              }}
            >
              <SelectTrigger aria-label="Environment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {environments.map((env) => (
                  <SelectItem key={env} value={env}>
                    {ENVIRONMENT_LABELS[env]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {isProd && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
              <Checkbox
                id="confirm-prod"
                checked={confirmProd}
                onCheckedChange={(v) => { setConfirmProd(v); }}
              />
              <Label htmlFor="confirm-prod" className="text-sm font-normal leading-snug">
                I understand this goes live to real users
              </Label>
            </div>
          )}
          <ErrorLine message={error} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setOpen(false); }}>
            Cancel
          </Button>
          <Button onClick={() => { void handleDeploy(); }} disabled={!canDeploy}>
            {deploy.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Deploy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
